import { useMutation, useQuery } from '@tanstack/react-query';
import { type ReactElement, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getBuildById } from '@/features/builds/data/builds';
import {
  deleteExecutionRun,
  getExecutionRunById,
} from '@/features/executionRuns/data/executionRuns';
import { querySuiteExecutions } from '@/features/executions/data';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { getResultsFileBySlug } from '@/features/uploads/data';
import { NotFoundScreen } from '@/pages/NotFoundScreen';
import { useNotifications } from '@/providers/notificationHooks';
import {
  gridPaths,
  invalidateExecutionRuns,
  invalidateGridQuery,
  queryKeys,
  useAccessToken,
  useInvalidateQueries,
} from '@/shared/hooks';
import { buildEqFilterValue } from '@/ui/composites/grid/gridFilters';
import { DetailPageScaffold } from '@/ui/composites/scaffolds';
import { usePageTitle } from '@/ui/hooks/usePageTitle';
import { useBreadcrumbs } from '@/ui/layout/breadcrumbsContext';
import { ExecutionRunDetailContent } from './ExecutionRunDetailContent';
import type { AuthSession } from '@/auth/types';
import type { ApiError } from '@/shared/data/client';
import type {
  BuildDto,
  ExecutionRunDto,
  QueryResult,
  ResultsFileDto,
  SuiteExecutionDto,
} from '@flowqa/shared';

type ExecutionRunDetailPageProps = { session: AuthSession | null };

export const ExecutionRunDetailPage = ({ session }: ExecutionRunDetailPageProps): ReactElement => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const token = useAccessToken();
  const { notify } = useNotifications();
  const invalidateQueries = useInvalidateQueries();
  const { setOverrideItems, clearOverride } = useBreadcrumbs();
  const searchId = useMemo(() => (id ?? '').trim(), [id]);
  const lastBreadcrumbKey = useRef<string | null>(null);

  const executionRunQuery = useQuery<ExecutionRunDto, ApiError>({
    queryKey: queryKeys.executionRunDetail(searchId || null, selectedOrganisationId ?? null),
    enabled: Boolean(token && searchId),
    queryFn: async () => await getExecutionRunById(searchId, selectedOrganisationId ?? undefined),
  });

  const { data: executionRun, isPending, isError, error } = executionRunQuery;
  usePageTitle(executionRun?.name ?? 'Execution Run');
  const resultsFileSlug = executionRun?.resultsFileSlug ?? null;
  const resultsFileQuery = useQuery<ResultsFileDto | null, ApiError>({
    queryKey: queryKeys.resultsFileDetail(resultsFileSlug, selectedOrganisationId ?? null),
    enabled: Boolean(token && resultsFileSlug),
    queryFn: async () =>
      resultsFileSlug
        ? await getResultsFileBySlug(resultsFileSlug, selectedOrganisationId ?? undefined)
        : null,
  });
  const resultsFile = resultsFileQuery.data ?? null;
  const buildId = resultsFile?.buildId ?? null;
  const buildQuery = useQuery<BuildDto | null, ApiError>({
    queryKey: queryKeys.buildDetail(buildId, selectedOrganisationId ?? null),
    enabled: Boolean(token && buildId),
    queryFn: async () =>
      buildId ? await getBuildById(buildId, selectedOrganisationId ?? undefined) : null,
  });
  const build = buildQuery.data ?? null;
  const suiteExecutionsQuery = useQuery<QueryResult<SuiteExecutionDto>, ApiError>({
    queryKey: queryKeys.dataGrid(
      'suite-executions/execution-run',
      executionRun?.id ?? null,
      executionRun?.projectId ?? null,
      selectedOrganisationId ?? null,
    ),
    enabled: Boolean(token && executionRun?.id),
    queryFn: async () =>
      await querySuiteExecutions(
        executionRun?.projectId ?? null,
        selectedOrganisationId ?? undefined,
        {
          page: 1,
          pageSize: 100,
          sorts: [{ field: 'timestamp', direction: 'desc' }],
          filters: buildEqFilterValue('runId', executionRun?.id ?? null),
        },
      ),
  });
  const suiteExecutions = suiteExecutionsQuery.data?.items ?? [];
  const deleteExecutionRunMutation = useMutation<{ id: string }, ApiError, { id: string }>({
    mutationFn: async (input) => {
      await deleteExecutionRun({ id: input.id }, selectedOrganisationId);
      return { id: input.id };
    },
    onSuccess: async () => {
      await invalidateQueries([
        invalidateExecutionRuns(selectedOrganisationId ?? null, selectedProjectId ?? null),
        invalidateGridQuery(gridPaths.executionRuns),
        invalidateGridQuery(gridPaths.suiteExecutions),
        invalidateGridQuery(gridPaths.testExecutions),
      ]);
      notify({ message: 'Execution run deleted.', tone: 'green' });
      void navigate('/execution-runs', { replace: true });
    },
    onError: (deleteError) => {
      notify({
        message: deleteError.message,
        tone: 'red',
      });
    },
  });

  const hasInvalidId = !searchId;
  const showNotFound = hasInvalidId || (!isPending && executionRunQuery.isFetched && !executionRun);

  useEffect(() => {
    if (!executionRun) return;
    const runProjectId = executionRun.projectId;
    if (!selectedProjectId || runProjectId !== selectedProjectId) {
      setSelectedProjectId(runProjectId);
    }
  }, [executionRun, navigate, selectedProjectId, setSelectedProjectId]);

  useEffect(() => {
    if (!executionRun) return () => undefined;
    const nextKey = executionRun.id;
    if (lastBreadcrumbKey.current === nextKey) return () => undefined;
    lastBreadcrumbKey.current = nextKey;
    setOverrideItems([
      { label: 'Execution Runs', href: '/execution-runs' },
      { label: executionRun.id },
    ]);
    return () => {
      clearOverride();
      lastBreadcrumbKey.current = null;
    };
  }, [clearOverride, executionRun, setOverrideItems]);

  if (showNotFound) {
    return (
      <NotFoundScreen
        title="Execution run not found"
        body="This execution run does not exist or you no longer have access to it."
        backTo="/execution-runs"
        backLabel="Back to execution runs"
      />
    );
  }

  const handleDeleteExecutionRun = async (): Promise<void> => {
    if (!executionRun) return;
    await deleteExecutionRunMutation.mutateAsync({ id: executionRun.id });
  };

  return (
    <DetailPageScaffold
      session={session}
      sessionMessage="Sign in to view execution run details."
      organisationId={selectedOrganisationId}
      requireProject={false}
      projectMessage="Select a project to view execution run details."
      projectId={selectedProjectId}
      isPending={isPending}
      isError={isError}
      error={error}
      errorMessage="Failed to load execution run"
      data={executionRun}
      loadingFallback={<></>}
    >
      {(resolvedExecutionRun) => {
        return (
          <ExecutionRunDetailContent
            executionRun={resolvedExecutionRun}
            build={build}
            suiteExecutions={suiteExecutions}
            suiteExecutionsLoading={suiteExecutionsQuery.isLoading}
            onDelete={handleDeleteExecutionRun}
            deleteLoading={deleteExecutionRunMutation.isPending}
          />
        );
      }}
    </DetailPageScaffold>
  );
};
